import { createSlice } from "@reduxjs/toolkit";
import LogRocket from "../util/LogRocketUtil";
import {
  AddStep,
  NextCurrentId,
  // DC-FE-ONPLAYERACTION-CLEANUP-001: OnPlayerAction removed - backend handles turn progression
} from "../common/game/GameControl";
import { GetNewDeck } from "../common/game/CommonGame";

function __filterPlayersHandler(payload) {
  if (!payload || !Array.isArray(payload)) {
    return [];
  }
  return payload.filter((player) => {
    return !player.IsFolded && !player.IsDisconnected;
  });
}

export const gameStateSlice = createSlice({
  name: "gameState",
  initialState: {
    GameCode: "",
    ActivePlayers: [],
    FilteredPlayers: [],
    CommunityCards: [],
    HandSteps: [],
    Deck: [],
    // DC-MULTITAB: Track if this tab is the active session for this player/game
    IsSessionActive: true,
  },
  reducers: {
    setMeetingId: (state, action) => {
      state.MeetingId = action.payload;
    },
    // DC-MULTITAB: Deactivate session when another tab takes over
    sessionDeactivated: (state) => {
      state.IsSessionActive = false;
    },
    // DC-MULTITAB: Activate session (e.g., on fresh join or reconnect)
    sessionActivated: (state) => {
      state.IsSessionActive = true;
    },
    setGameState: (state, action) => {
      state.GameCode = action.payload.GameCode;
      state.ActivePlayers = action.payload.ActivePlayers;
      state.Deck = action.payload.Deck;
      state.CommunityCards = action.payload.CommunityCards;
      state.HandSteps = action.payload.HandSteps;
      state.Id = action.payload.Id;
      state.DealerId = action.payload.DealerId;
      state.CurrentId = action.payload.CurrentId;
      state.BetStatus = action.payload.BetStatus;
      state.BetStatusIndex = action.payload.BetStatusIndex;
      state.CurrentBet = action.payload.CurrentBet;
      state.EndDate = action.payload.EndDate;
      state.GameCreatorId = action.payload.GameCreatorId;
      state.GameHand = action.payload.GameHand;
      state.IsEnded = action.payload.IsEnded;
      state.IsInvitesOnly = action.payload.IsInvitesOnly;
      state.IsLocked = action.payload.IsLocked;
      state.IsRoundSettlement = action.payload.IsRoundSettlement;
      state.MeetingId = action.payload.MeetingId;
      state.MeetingMinutes = action.payload.MeetingMinutes;
      state.NumberOfCommunities = action.payload.NumberOfCommunities;
      state.PotSize = action.payload.PotSize;
      state.Round = action.payload.Round;
      state.VideoChatAllowed = action.payload.VideoChatAllowed;
      state.FilteredPlayers = __filterPlayersHandler(
        action.payload.ActivePlayers
      );
    },
    /**
     * DC-FE-PLAYERLEFT-CONSUME-PAYLOAD-001:
     * Apply CurrentId/DealerId/GameCreatorId from backend. Backend is source of truth.
     * @param {*} state
     * @param {*} action : object with Index, LeftId, CurrentId, DealerId, GameCreatorId
     */
    playerLeft: (state, action) => {
      const { Index, CurrentId, DealerId, GameCreatorId } = action.payload || {};

      // Safety guard: skip if player index is invalid
      if (Index === undefined || Index === null || !state.ActivePlayers[Index]) {
        return;
      }

      state.ActivePlayers.splice(Index, 1);

      // Keep FilteredPlayers in sync with ActivePlayers,
      // using the same helper as sitout/rejoin.
      state.FilteredPlayers = __filterPlayersHandler(state.ActivePlayers);

      // Apply backend-sent IDs (only if truthy, as a safety net)
      if (CurrentId) {
        state.CurrentId = CurrentId;
      }
      if (DealerId) {
        state.DealerId = DealerId;
      }
      if (GameCreatorId) {
        state.GameCreatorId = GameCreatorId;
      }

      LogRocket.log(`${Index}th Player Left Game`, state);
    },
    // DC-PLAYER-DISCONNECT-STATE-FIX-001: Only mark player as disconnected.
    // Do NOT transfer CurrentId or DealerId here — let backend be single source of truth.
    playerDisconnected: (state, action) => {
      // Safety guard: skip if player index is invalid
      if (!state.ActivePlayers[action.payload]) return;
      
      state.ActivePlayers[action.payload].IsDisconnected = true;
      state.FilteredPlayers = state.ActivePlayers;
      LogRocket.log(`${action.payload}th Player Disconnected`, state);
    },
    playerConnected: (state, action) => {
      let index = state.ActivePlayers.findIndex(
        (x) => x.PlayerId === action.payload.UserId
      );
      if (index !== -1) {
        state.ActivePlayers[index].ConnectionId = action.payload.ConnectionId;
        state.ActivePlayers[index].IsDisconnected = false;
        state.FilteredPlayers = state.ActivePlayers;
      }
      LogRocket.log(`${action.payload}th Player Connected`, state);
    },
    playerJoin: (state, action) => {
      let index = state.ActivePlayers.findIndex(
        (x) => x.PlayerId === action.payload.userId
      );
      if (index !== -1) return;
      state.ActivePlayers.push({
        PlayerId: action.payload.userId,
        PlayerImage: action.payload.playerImage,
        PlayerName: action.payload.userName,
        ConnectionId: action.payload.connectionId,
        PlayerNetStatusFinal: 0,
        PlayerCards: [],
        PlayerAmount: 0,
        IsSitOut: false,
        IsFolded: false,
        IsRealTimeChat: true,
        IsRealTimeChatForMic: true,
        IsDisconnected: false,
        CurrentRoundStatus: 0,
        Balance: 0,
        LastActionPerformed: "",
      });
      state.FilteredPlayers = state.ActivePlayers;
      if (state.ActivePlayers.length === 2) {
        state.CurrentId = state.ActivePlayers[1].PlayerId;
      }
      LogRocket.log(`${action.payload.userName} Joined joined the game`, state);
    },
    /**
     *
     * @param {*} state
     * @param {*} action; payload = { draggingCard: {}, Index, type }
     */
    passCard: (state, action) => {
      action.payload.draggingCards.forEach((draggingCard) => {
        // Safety guard: skip if source player index is invalid
        if (draggingCard.Type === 0) {
          if (!state.ActivePlayers[draggingCard.Index]) return;
          state.ActivePlayers[draggingCard.Index].PlayerCards =
            state.ActivePlayers[draggingCard.Index].PlayerCards.filter(
              (x) => x.Value !== draggingCard.Value
            );
        } else {
          state.CommunityCards = state.CommunityCards.filter(
            (x) => x.Value !== draggingCard.Value
          );
        }

        // Safety guard: skip if target player index is invalid
        if (action.payload.Type === 0) {
          if (!state.ActivePlayers[action.payload.Index]) return;
          state.ActivePlayers[action.payload.Index].PlayerCards.push({
            Value: draggingCard.Value,
            Presentation: draggingCard.Presentation,
          });
        } else {
          state.CommunityCards.push({
            Value: draggingCard.Value,
            Presentation: 0,
            CommunityIndex: action.payload.Index,
          });
        }
      });
      LogRocket.log(
        `Pass ${action.payload.draggingCards.length} cards to ${
          action.payload.Index
        }th ${action.payload.Type === 0 ? "Player" : "Community"}`,
        state
      );
      return state;
    },
    bet: (state, action) => {
      state.ActivePlayers[action.payload.Index].PlayerAmount -=
        action.payload.Amount;
      state.PotSize += action.payload.Amount;
      state.ActivePlayers[action.payload.Index].CurrentRoundStatus +=
        action.payload.Amount;

      let actionMsg = " bet: " + action.payload.Amount;
      if (state.CurrentBet < action.payload.Amount) {
        actionMsg =
          " raised by: " +
          (action.payload.Amount - state.CurrentBet) +
          "- bet: " +
          action.payload.Amount;

        state.CurrentBet = action.payload.Amount;
      }
      state.ActivePlayers[action.payload.Index].LastActionPerformed = actionMsg;
      AddStep(state, action.payload.Index, actionMsg, "Bet");
      // DC-FE-ONPLAYERACTION-CLEANUP-001: Apply turn state from backend
      state.CurrentId = action.payload.CurrentId;
      state.BetStatus = action.payload.BetStatus;
      state.BetStatusIndex = action.payload.BetStatusIndex;
      // US-002-S1D: Apply authoritative betting state from backend when present
      if (typeof action.payload.CurrentBet === "number") {
        state.CurrentBet = action.payload.CurrentBet;
      }
      if (Array.isArray(action.payload.CurrentRoundStatusArray)) {
        const arr = action.payload.CurrentRoundStatusArray;
        const len = Math.min(arr.length, state.ActivePlayers.length);
        for (let i = 0; i < len; i++) {
          state.ActivePlayers[i].CurrentRoundStatus = arr[i];
        }
      }
      LogRocket.log(
        `${action.payload.Index}th Player betted ${action.payload.Amount}`,
        state
      );
    },
    addToPot: (state, action) => {
      state.ActivePlayers[action.payload.Index].PlayerAmount -=
        action.payload.Amount;
      state.PotSize += action.payload.Amount;
      state.ActivePlayers[action.payload.Index].LastActionPerformed =
        " added " + action.payload.Amount + "$ to pot";
      AddStep(
        state,
        action.payload.Index,
        "added " + action.payload.Amount + "$ to pot",
        "AddToPot"
      );
      LogRocket.log(
        `${action.payload.Index}th Player added ${action.payload.Amount} to pot`
      );
    },
    ante: (state, action) => {
      state.ActivePlayers.filter(
        (player) => !player.IsFolded && !player.IsDisconnected
      ).forEach((player) => {
        player.PlayerAmount -= action.payload.Amount;
        state.PotSize += action.payload.Amount;
      });
      state.ActivePlayers[action.payload.Index].LastActionPerformed =
        "Ante " + action.payload.Amount;
      AddStep(
        state,
        action.payload.Index,
        "anted " + action.payload.Amount + "$",
        "Ante"
      );
      LogRocket.log(`Anted ${action.payload.Amount} to Pot`, state);
    },
    call: (state, action) => {
      const betamount = state.CurrentBet;
      const currentPlayer = state.ActivePlayers[action.payload.Index];
      const currentplayerbet = betamount - currentPlayer.CurrentRoundStatus;
      currentPlayer.CurrentRoundStatus = betamount;
      const actionMsg = " called with " + currentplayerbet;
      currentPlayer.PlayerAmount -= currentplayerbet;
      state.PotSize += currentplayerbet;
      currentPlayer.LastActionPerformed = actionMsg;
      AddStep(state, action.payload.Index, actionMsg, "Call");
      // DC-FE-ONPLAYERACTION-CLEANUP-001: Apply turn state from backend
      state.CurrentId = action.payload.CurrentId;
      state.BetStatus = action.payload.BetStatus;
      state.BetStatusIndex = action.payload.BetStatusIndex;

      // US-002-S1C: Apply authoritative betting state if present (backward compatible)
      if (typeof action.payload.CurrentBet === "number") {
        state.CurrentBet = action.payload.CurrentBet;
      }
      if (Array.isArray(action.payload.CurrentRoundStatusArray)) {
        const arr = action.payload.CurrentRoundStatusArray;
        const len = Math.min(arr.length, state.ActivePlayers.length);
        for (let i = 0; i < len; i++) {
          state.ActivePlayers[i].CurrentRoundStatus = arr[i];
        }
      }

      LogRocket.log(
        `${action.payload.Index}th Player Called ${currentplayerbet}`,
        state
      );
    },
    cancelHand: (state, action) => {
      state.ActivePlayers.forEach((player) => {
        if (player.PlayerAmount < 0) state.PotSize += player.PlayerAmount;
        player.PlayerAmount = 0;
        player.Balance = 0;
        player.CurrentRoundStatus = 0;
        player.PlayerCards = [];
        player.LastActionPerformed = "";
        if (!player.IsSitOut) player.IsFolded = false;
      });
      state.BetStatus = "New Hand. No bet yet.";
      state.CurrentBet = 0;
      state.CommunityCards = [];
      state.Deck = GetNewDeck();
      let index = state.ActivePlayers.findIndex(
        (x) => x.PlayerId === state.DealerId
      );
      AddStep(state, index, "cancelled hand", "Cancel_Hand");
      state.GameHand += 1;
      state.Round = 0;
      state.HandSteps.push({
        HandId: state.GameHand,
        BettingRounds: [],
      });
      let dealerIndex = state.ActivePlayers.findIndex(
        (x) => x.PlayerId === state.DealerId
      );
      state.CurrentId = NextCurrentId(state, dealerIndex);
      LogRocket.log(`Cancelled Hand`);
    },
    check: (state, action) => {
      state.ActivePlayers[action.payload.Index].LastActionPerformed = " Pass";
      AddStep(state, action.payload.Index, " pass", "check");
      // DC-FE-ONPLAYERACTION-CLEANUP-001: Apply turn state from backend
      state.CurrentId = action.payload.CurrentId;
      state.BetStatus = action.payload.BetStatus;
      state.BetStatusIndex = action.payload.BetStatusIndex;

      // US-002-S1B: Apply authoritative betting state if present (backward compatible)
      if (typeof action.payload.CurrentBet === "number") {
        state.CurrentBet = action.payload.CurrentBet;
      }
      if (Array.isArray(action.payload.CurrentRoundStatusArray)) {
        const arr = action.payload.CurrentRoundStatusArray;
        const len = Math.min(arr.length, state.ActivePlayers.length);
        for (let i = 0; i < len; i++) {
          state.ActivePlayers[i].CurrentRoundStatus = arr[i];
        }
      }

      LogRocket.log(`${action.payload.Index}th Player checked`, state);
    },
    sitout: (state, action) => {
      state.ActivePlayers[action.payload.Index].LastActionPerformed = " Sitout";
      AddStep(state, action.payload.Index, "sitout", "Sitout");
      state.ActivePlayers[action.payload.Index].IsSitOut = true;
      state.ActivePlayers[action.payload.Index].IsFolded = true;
      state.ActivePlayers[action.payload.Index].PlayerCards.forEach(
        (playerCard) => (playerCard.Presentation = 1)
      );
      // DC-FE-ONPLAYERACTION-CLEANUP-001: Apply turn state from backend
      state.CurrentId = action.payload.CurrentId;
      state.BetStatus = action.payload.BetStatus;
      state.BetStatusIndex = action.payload.BetStatusIndex;
      // DC-5CDRAW-SITOUT-REGRESSION-001: Recalculate FilteredPlayers after sitout
      state.FilteredPlayers = __filterPlayersHandler(state.ActivePlayers);
      LogRocket.log(`${action.payload.Index}th Player sit out`, state);
    },
    rejoin: (state, action) => {
      state.ActivePlayers[action.payload].LastActionPerformed = " Rejoin";
      AddStep(state, action.payload, "rejoined", "Rejoin");
      state.ActivePlayers[action.payload].IsSitOut = false;
      
      // If between hands (no cards dealt), allow immediate participation
      const isBetweenHands = !state.ActivePlayers.some(p => p.PlayerCards.length > 0);
      if (isBetweenHands) {
        state.ActivePlayers[action.payload].IsFolded = false;
      }
      // DC-5CDRAW-SITOUT-REGRESSION-001: Recalculate FilteredPlayers after rejoin
      state.FilteredPlayers = __filterPlayersHandler(state.ActivePlayers);
      
      LogRocket.log(`${action.payload}th Player rejoined`, state);
    },
    fold: (state, action) => {
      state.ActivePlayers[action.payload.Index].LastActionPerformed = " Fold";

      AddStep(state, action.payload.Index, "folded", "Fold");
      state.ActivePlayers[action.payload.Index].IsFolded = true;
      state.ActivePlayers[action.payload.Index].PlayerCards.forEach(
        (playerCard) => (playerCard.Presentation = 1)
      );
      // DC-FE-ONPLAYERACTION-CLEANUP-001: Apply turn state from backend
      state.CurrentId = action.payload.CurrentId;
      state.BetStatus = action.payload.BetStatus;
      state.BetStatusIndex = action.payload.BetStatusIndex;

      // US-002-S1E: Apply authoritative betting state from backend (backward-compat)
      if (typeof action.payload.CurrentBet === "number") {
        state.CurrentBet = action.payload.CurrentBet;
      }
      if (Array.isArray(action.payload.CurrentRoundStatusArray)) {
        const arr = action.payload.CurrentRoundStatusArray;
        const len = Math.min(arr.length, state.ActivePlayers.length);
        for (let i = 0; i < len; i++) {
          state.ActivePlayers[i].CurrentRoundStatus = arr[i];
        }
      }

      state.FilteredPlayers = __filterPlayersHandler(state.ActivePlayers);
      LogRocket.log(`${action.payload.Index}th Player folede`, state);
    },
    take: (state, action) => {
      state.ActivePlayers[action.payload.Index].LastActionPerformed =
        " Took $" + action.payload.Amount;
      state.ActivePlayers[action.payload.Index].PlayerAmount +=
        action.payload.Amount;
      state.PotSize -= action.payload.Amount;
      AddStep(
        state,
        action.payload.Index,
        state.ActivePlayers[action.payload.Index].LastActionPerformed,
        "Take"
      );
      LogRocket.log(
        `${action.payload.Index}th Player took $${action.payload.Amount}`,
        state
      );
    },
    discard: (state, action) => {
      state.ActivePlayers[action.payload.Index].LastActionPerformed =
        " Discarded " + action.payload.selectedCards.length + " cards";
      action.payload.selectedCards.forEach((card) => {
        if (card.Type === 0)
          state.ActivePlayers[action.payload.Index].PlayerCards =
            state.ActivePlayers[action.payload.Index].PlayerCards.filter(
              (obj) => obj.Value !== card.Value
            );
        else {
          state.CommunityCards = state.CommunityCards.filter(
            (obj) => obj.Value !== card.Value
          );
        }
      });
      AddStep(
        state,
        action.payload.Index,
        state.ActivePlayers[action.payload.Index].LastActionPerformed,
        "Discard"
      );
      LogRocket.log(
        `${action.payload.Index}th Player discarded ${action.payload.selectedCards.length} cards`,
        state
      );
    },
    returnToDeck: (state, action) => {
      state.ActivePlayers[action.payload.Index].LastActionPerformed =
        " Returned " + action.payload.selectedCards.length + " cards";
      action.payload.selectedCards.forEach((card) => {
        if (card.Type === 0) {
          state.ActivePlayers[action.payload.Index].PlayerCards =
            state.ActivePlayers[action.payload.Index].PlayerCards.filter(
              (obj) => obj.Value !== card.Value
            );
        } else if (card.Type === 1) {
          state.CommunityCards = state.CommunityCards.filter(
            (obj) => obj.Value !== card.Value
          );
        }
        state.Deck.push(card.Value);
      });
      AddStep(
        state,
        action.payload.Index,
        state.ActivePlayers[action.payload.Index].LastActionPerformed,
        "ReturnToDeck"
      );
      LogRocket.log(
        `${action.payload.Index}th Player returned ${action.payload.selectedCards.length} cards to deck`,
        state
      );
    },
    show: (state, action) => {
      if (action.payload.selectedCards.length === 0) {
        state.ActivePlayers[action.payload.Index].PlayerCards.forEach(
          (card) => (card.Presentation = 0)
        );
      } else {
        action.payload.selectedCards.forEach((card) => {
          if (card.Type === 0) {
            let index = state.ActivePlayers[
              action.payload.Index
            ].PlayerCards.findIndex((x) => x.Value === card.Value);
            if (index !== -1)
              state.ActivePlayers[action.payload.Index].PlayerCards[
                index
              ].Presentation = 0;
          } else {
            state.CommunityCards.find(
              (x) => x.Value === card.Value
            ).Presentation = 0;
          }
        });
      }
      LogRocket.log(`${action.payload.Index}th Player showed cards`, state);
    },

    endHand: (state, action) => {
      state.IsRoundSettlement = true;
      AddStep(state, -1, "ended hand", "EndHand");

      state.ActivePlayers.forEach((obj) => {
        obj.PlayerNetStatusFinal = obj.PlayerNetStatusFinal + obj.PlayerAmount;
        obj.PlayerAmount = 0;
        obj.Balance = 0;
        obj.CurrentRoundStatus = 0;
        obj.LastActionPerformed = "";
        obj.PlayerCards = [];

        // Reset folded status if player is not sitting out
        if (!obj.IsSitOut) {
          obj.IsFolded = false;
        }
      });

      // Reset other round-specific states
      state.CommunityCards = [];
      state.CurrentBet = 0;
      state.Deck = GetNewDeck();
      state.GameHand += 1;
      state.Round = 0;
      state.HandSteps.push({
        HandId: state.GameHand,
        BettingRounds: [],
      });

      state.BetStatus = "New Hand. No bet yet.";

      let dealerIndex = state.ActivePlayers.findIndex(
        (x) => x.PlayerId === state.DealerId
      );
      console.log("endhand index in gameStateSlice", dealerIndex);
      state.CurrentId = NextCurrentId(state, dealerIndex);

      // ✅ Rebuild filtered players list after all reset operations
      state.FilteredPlayers = __filterPlayersHandler(state.ActivePlayers);

      LogRocket.log(`Ended hand`, state);
    },

    // endHand: (state, action) => {
    //   state.IsRoundSettlement = true;
    //   AddStep(state, -1, "ended hand", "EndHand");
    //   state.ActivePlayers.forEach((obj) => {
    //     obj.PlayerNetStatusFinal = obj.PlayerNetStatusFinal + obj.PlayerAmount;
    //     obj.PlayerAmount = 0;
    //     obj.Balance = 0;
    //     obj.CurrentRoundStatus = 0;
    //     obj.LastActionPerformed = "";
    //     obj.PlayerCards = [];
    //     //if you'r not sitout, set IsFolded as false
    //     if (!obj.IsSitOut) obj.IsFolded = false;
    //   });
    //   state.CommunityCards = [];
    //   state.CurrentBet = 0;
    //   state.Deck = GetNewDeck();
    //   state.GameHand += 1;
    //   state.Round = 0;
    //   state.HandSteps.push({
    //     HandId: state.GameHand,
    //     BettingRounds: [],
    //   });

    //   state.BetStatus = "New Hand. No bet yet.";
    //   let dealerIndex = state.ActivePlayers.findIndex(
    //     (x) => x.PlayerId === state.DealerId
    //   );
    //   console.log("endhand index in gameStateSlice", dealerIndex);
    //   state.CurrentId = NextCurrentId(state, dealerIndex);
    //   LogRocket.log(`Ended hand`, state);
    // },
    endGame: (state, action) => {
      state.IsEnded = true;
      LogRocket.log(`Ended game`);
    },
    toggleCamera: (state, action) => {
      // debugger;
      state.ActivePlayers[action.payload].IsRealTimeChat =
        !state.ActivePlayers[action.payload].IsRealTimeChat;
    },
    handleCamera: (state, action) => {
      state.ActivePlayers[action.payload.index].IsRealTimeChat =
        action.payload.value;
    },
    toggleMic: (state, action) => {
      // state.ActivePlayers[action.payload].IsRealTimeChatForMic =
      //   !state.ActivePlayers[action.payload].IsRealTimeChatForMic;
    },
    handleMic: (state, action) => {
      state.ActivePlayers[action.payload.index].IsRealTimeChatForMic =
        action.payload.value;
    },

    dealCards: (state, action) => {
      action.payload.dealCards.forEach((card) => {
        state.Deck = state.Deck.filter((x) => x !== card.Value);
        if (card.Type === 0) {
          // DC-5CDRAW-FIX-001: card.Index is always a FilteredPlayers index from backend
          // Always look up target player by PlayerId, then find in ActivePlayers
          const filteredPlayer = state.FilteredPlayers[card.Index];
          const player = state.ActivePlayers.find(
            (x) => x.PlayerId === filteredPlayer.PlayerId
          );

          if (player) {
            player.PlayerCards.push({
              Value: card.Value,
              Presentation: card.Presentation,
            });
          }
        } else {
          state.CommunityCards.push({
            Value: card.Value,
            Presentation: card.Presentation,
            CommunityIndex: card.Index,
          });
        }
      });
      let dealerIndex = state.ActivePlayers.findIndex(
        (x) => x.PlayerId === state.DealerId
      );
      state.ActivePlayers[dealerIndex].LastActionPerformed =
        action.payload.LastActionPerformed;
      AddStep(state, dealerIndex, action.payload.LastActionPerformed, "Deal");
      LogRocket.log(action.payload.LastActionPerformed, state);
    },
    passDeal: (state, action) => {
      state.DealerId = action.payload;
      if (state.Deck.length === 52) {
        let newDealerIndex = state.ActivePlayers.findIndex(
          (x) => x.PlayerId === state.DealerId
        );
        state.CurrentId = NextCurrentId(state, newDealerIndex);
      }
      LogRocket.log(`Passed Deal to ${action.payload}th Player`, state);
    },
    toggleLock: (state, action) => {
      state.IsLocked = !state.IsLocked;
    },
  },
});

export const {
  setMeetingId,
  setGameState,
  sessionDeactivated,
  sessionActivated,
  passCard,
  playerLeft,
  playerDisconnected,
  bet,
  take,
  addToPot,
  ante,
  call,
  cancelHand,
  check,
  discard,
  returnToDeck,
  show,
  endHand,
  fold,
  sitout,
  rejoin,
  endGame,
  toggleCamera,
  handleCamera,
  toggleMic,
  handleMic,
  dealCards,
  passDeal,
  playerConnected,
  toggleLock,
  playerJoin,
} = gameStateSlice.actions;

// DC-MULTITAB: Selector for IsSessionActive
export const selectIsSessionActive = (state) => state.gameState.IsSessionActive;

export default gameStateSlice.reducer;
